import { useEffect, useRef, useState } from 'react';

import { searchCommunesApi } from '@api-client';
import type { Commune } from '@shared/types/entities';

interface UseCommuneSearchOptions {
  debounceMs?: number;
  minLength?: number;
  department?: string;
  limit?: number;
}

interface UseCommuneSearchResult {
  communes: Commune[];
  isLoading: boolean;
  search: (q: string) => void;
  clear: () => void;
}

export function useCommuneSearch(options: UseCommuneSearchOptions = {}): UseCommuneSearchResult {
  const { debounceMs = 300, minLength = 2, department, limit = 10 } = options;
  const [communes, setCommunes] = useState<Commune[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const search = (q: string) => {
    clearTimeout(timerRef.current);

    if (!q || q.trim().length < minLength) {
      setCommunes([]);
      return;
    }

    timerRef.current = setTimeout(() => {
      setIsLoading(true);
      searchCommunesApi(q.trim(), department, limit)
        .then(setCommunes)
        .catch(() => setCommunes([]))
        .finally(() => setIsLoading(false));
    }, debounceMs);
  };

  const clear = () => {
    clearTimeout(timerRef.current);
    setCommunes([]);
    setIsLoading(false);
  };

  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  return { communes, isLoading, search, clear };
}
